import React, {useState, useRef, ChangeEvent, useCallback} from "react";
import BasicText from "../../../components/form-v2/basic-text";
import { IconButton } from "../../../features/management-v2/import/inputs";
import { FileUpload, FileCopy } from "@mui/icons-material";
import {noop} from "../../../lib/misc/noop";
import {ErrorText} from "./ErrorText";
import {platformFormAPI} from "../../../api/axiosConfig";

const ALLOWED_FILE_TYPES = [
    // Documents
    "application/pdf",          // .pdf
    "application/msword",       // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.oasis.opendocument.text", // .odt
    "text/plain",               // .txt
    "text/csv",                 // .csv

    // Spreadsheets
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.oasis.opendocument.spreadsheet", // .ods

    // Presentations
    "application/vnd.ms-powerpoint", // .ppt
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    "application/vnd.oasis.opendocument.presentation", // .odp

    // Images
    "image/jpeg",               // .jpg, .jpeg
    "image/png",                // .png
    "image/gif",                // .gif
];


export type StagedUpload = {
    id: string;
    name: string;
}

type MultiFileUploadProps = {
    clientID?: string;
    onAdd?: (upload: StagedUpload) => void;
}


export function MultiFileUpload({ clientID, onAdd = noop }: MultiFileUploadProps) {
    const [currentFile, setCurrentFile] = useState<File | null>(null);
    const [currentName, setCurrentName] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSelectFile = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const prevFileName = currentFile?.name

        const file = e.target.files?.[0] ?? null;
        if (file) {
            setCurrentFile(file);
            if (!currentName || currentName == prevFileName) {
                setCurrentName(file.name);
            }
        }
    };


    const handleUpload = useCallback(async () => {
        if (!currentFile) return;
        setLoading(true);
        setError(null);

        try {
            const form = new FormData();
            form.append("name", currentName || currentFile.name);
            form.append("file", currentFile);
            const uploadRes = await platformFormAPI.post<{id: string}>("api/siren/file-attachments/upload", form);
            const uploadID = uploadRes.data.id;

            onAdd({id: uploadID, name: currentName || currentFile.name});

            setCurrentFile(null);
            setCurrentName("");
            if (fileInputRef.current) fileInputRef.current.value = "";

        } catch (e: any) {
            const fieldErr = e?.response?.data?.errors?.file?.[0];
            setError(fieldErr ?? "Error uploading file");
        } finally {
            setLoading(false);
        }

    }, [currentFile, currentName, clientID, onAdd]);


    return (
        <div className="flex flex-col gap-4">
            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept={ALLOWED_FILE_TYPES.join(",")}
            />

            {/* File selection / upload */}
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <BasicText
                        value={currentName}
                        onChange={setCurrentName}
                        placeholder="File Name"
                    />
                </div>
                <div>
                    <IconButton
                        text={currentFile ? currentFile.name : "Select File"}
                        onClick={handleSelectFile}
                        icon={<FileCopy />}
                        disabled={loading}
                    />
                </div>
                <div>
                    <IconButton
                        text="Upload"
                        onClick={handleUpload}
                        icon={<FileUpload />}
                        loading={loading}
                        color="green"
                        disabled={loading || !currentFile}
                    />
                </div>
            </div>

            {/* Upload Error Message*/}
            <ErrorText errors={error}/>


        </div>
    );
}
